const { getCredential } = require("./auth/getCredentials");
const { TableClient, TableTransaction } = require("@azure/data-tables");
const { AderantAzureStorageError } = require("./utils/errors");
const promise = require('bluebird');
const cache = require('node-cache');
const _ = require('lodash');

let clientCache = new cache({ stdTTL: 3600, useClones: false });

/**
 * Get the client for the current context. This client will be cached for 1 hour upon creation.
 * If the client does not exist in the cache, it will be created using the options passed in
 * through the options.
 * @param {TableClientOptions} tableClientOptions The options to get the client for
 * @returns @type {TableClient} Client
 */
const getClient = (tableClientOptions) => {
    const cacheItemName = `${tableClientOptions.accountName}-${tableClientOptions.tableName}`
    let cacheItem = clientCache.get(cacheItemName);
    if (!cacheItem) {
        const credential = getCredential(tableClientOptions, 'table');
        const tableClient = new TableClient(`https://${tableClientOptions.accountName}.table.core.windows.net`, tableClientOptions.tableName, credential);
        clientCache.set(cacheItemName, tableClient);
        return tableClient;
    }
    return cacheItem;
}

/**
 * Table store for accessing azure storage tables.
 */
class TableStore {
    /**
     * Creates a tablestore instance. This is used for accessing the table stores in an Azure Table within a storage account.
     *
     * The required parameters are one of the following
     *      tableName
     *      accountName
     *
     * The optional combinations for authentication are the following:
     *      accountKey and accountName (for a shared key credential)
     *      clientSecret, clientId, tenantId (for a client secret credential)
     *      useIdentity set to true.
     *
     * useIdentity will be used if available. If not, then we will check for the account key. Finally it will fall through to clientId, clientSecret and tenantId.
     *
     * @param {object} tableClientOptions queue client options for client creation.
     *  Object structure:
     *  {
     *      accountKey: 'string',
     *      accountName: 'string',
     *      tableName: 'string',
     *      useIdentity: bool,
     *      clientSecret: 'string',
     *      clientId: 'string',
     *      tenantId: 'string'
     *  }
     */
    constructor(tableClientOptions) {
        if (!tableClientOptions) throw new AderantAzureStorageError('A tableClientOptions parameter is required when initializing the TableStore class.');
        this.accountKey = tableClientOptions.accountKey;
        this.accountName = tableClientOptions.accountName;
        this.tenantId = tableClientOptions.tenantId;
        this.tableName = tableClientOptions.tableName;
        this.useIdentity = tableClientOptions.useIdentity;
        /** @type {TableClient}*/
        this.tableClient = getClient(tableClientOptions);
    }    

    /**Get the name of the storage account this TableStore belongs to. 
     * @returns {string} The name of the storage account.
     */
    getAccountName = () => {
        return this.accountName;
    }

    /**Get the name of the table this TableStore is connected to. 
     * @returns {string} The name of the queue.
     */  
    getTableName = () => {
        return this.tableClient.tableName;
    }

    /**
     * query the table store for entities that match the given OData query.
     * @param {ODataQuery} query
     */
    queryEntities = async (query) => {
        const queryResults = this.tableClient.listEntities({ queryOptions: { filter: query } });
        const results = []
        for await (let entity of queryResults) {
            results.push(entity);
        }
        return results;
    }

    /**
     * Get an entity by partitionKey and rowKey.
     * @param {string} partitionKey The partition key for the record to get
     * @param {string} rowKey The row key for the record to get
     * @returns
     */
    get = async (partitionKey, rowKey) => {
        if (!partitionKey || rowKey === undefined) {
            throw new AderantAzureStorageError(`A partitionKey is required for a get call. A rowKey must also be defined, although it can be null or an empty string.`);
        }
        let queryResult = null;
        try {
            queryResult = await this.tableClient.getEntity(partitionKey, rowKey);
        } catch (err) {
            if (err.statusCode === 404) {
                return queryResult;
            }
            throw err;
        }
        return queryResult;
    }

    /**
     * Deletes the given entity with the partitionkey and rowkey as properties on the object. It is recommended
     * that you get the entity before you try to delete it from the backend store.
     * @param {object} entity Entity to delete from the backend store. A partitionKey and rowKey property is required.
     * @returns
     */
    delete = async (entity) => {
        try {
            const deleteResult = await this.tableClient.deleteEntity(entity.partitionKey, entity.rowKey);
            return deleteResult;
        } catch (err) {
            if (err.statusCode === 404) return null;
            throw err;
        }
    }

    /**
     * Deletes the array of entities from the backend store. A partitionKey and rowKey is required on every entity.
     * @param {object[]} entities Entities to delete from the backend store. A partitionKey and rowKey is required for all entities.
     */
    deleteEntities = async (entities) => {
        if (!entities || entities.length == 0) return [];
        const isValid = entities.every(x => x.partitionKey && x.rowKey !== undefined);
        if (!isValid) throw new AderantAzureStorageError(`You must provide entities with a partitionKey and rowKey defined`);
        const transactionResults = await promise.map(entities, entity => {
            return this.delete(entity);
        }, {concurrency: 8});
        return transactionResults;
    }

    /**
     * Upsert entity into the backend store. A partitionKey and rowKey is required on the entity.
     * @param {object} entity Entity to upsert into the backend data store. partitionKey and rowKey are required on the object.
     * @returns
     */
    upsertEntity = async (entity) => {
        if (!entity || !entity.partitionKey || entity.rowKey === undefined) throw new AderantAzureStorageError(`You must provide an entity with a partitionKey and rowKey defined.`);
        let result = await this.tableClient.upsertEntity(entity);
        return result;
    }

    /**
     * Upsert entities into the backend store. A partitionKey and rowKey must be defined on all entities.
     * @param {{partitionKey: string, rowKey: string}[]} entities Array of entities to upsert into the backend data store. A partitionKey and rowKey are required to be defined on the object.
     * @returns
     */
    upsertEntities = async (entities) => {
        if (!entities || entities.length == 0) return [];
        const isValid = entities.every(x => x.partitionKey && x.rowKey !== undefined);
        if (!isValid) throw new AderantAzureStorageError(`You must provide entities with a partitionKey and rowKey defined`);
        
        //Each batch transaction must be limited to one partition key.
        const groupedByPartitionKey = _.groupBy(entities, "partitionKey");
        //TableStorage has a limit of 100 rows per batch.
        const chunkedBy100 = Object.values(groupedByPartitionKey).flatMap(entries => _.chunk(entries, 100));

        const transactions = Object.values(chunkedBy100).map(entries => { return async () => {
            const transaction = new TableTransaction();
            entries.forEach(t => transaction.upsertEntity(t));
            return await this.tableClient.submitTransaction(transaction.actions);
        }});
        
        const transactionResults = await promise.map(transactions, async transaction => await transaction(), { concurrency: 8 })
        
        return transactionResults;
    }

    /**
     * List all entities in the backend data store. Use with Caution, for larger tables you could run into memory problems.
     * @returns
     */
    list = async () => {
        const listResults = this.tableClient.listEntities();
        const results = []
        for await (const entity of listResults) {
            results.push(entity);
        }
        return results;
    }

}

module.exports = { TableStore };